import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Squares2X2Icon,
  CreditCardIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { cn } from "../../utils/cn";
import { useAuthStore } from "../../store/authStore";
import { useSubscriptionStore } from "../../store/subscriptionStore";
import { ProjectsView } from "./ProjectsView";
import { BillingView } from "./Billing/BillingView";
import { SettingsView } from "./Settings/SettingsView";
import { SupportView } from "./Support/SupportView";

const navigation = [
  { name: "Projects", section: "projects", icon: Squares2X2Icon },
  { name: "Billing", section: "billing", icon: CreditCardIcon },
  { name: "Settings", section: "settings", icon: Cog6ToothIcon },
];

const bottomNavigation = [
  { name: "Support", section: "support", icon: QuestionMarkCircleIcon },
];

export const DashboardLayout: React.FC<{ section: string }> = ({ section }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuthStore();
  const { checkSubscriptionStatus, isSubscribed } = useSubscriptionStore();

  // Check subscription status when dashboard loads
  useEffect(() => {
    if (user?.id) {
      checkSubscriptionStatus(user.id);
    }
  }, [user?.id, checkSubscriptionStatus]);

  if (!user) {
    navigate("/?mode=signin", { replace: true });
    return null;
  }

  const handleNavigation = (section: string) => {
    searchParams.set("mode", "dashboard");
    searchParams.set("section", section);
    setSearchParams(searchParams);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/?mode=signin", { replace: true });
  };

  const renderSection = () => {
    switch (section) {
      case "billing":
        return <BillingView />;
      case "settings":
        return <SettingsView />;
      case "support":
        return <SupportView />;
      default:
        return <ProjectsView />;
    }
  };

  const getSectionTitle = () => {
    switch (section) {
      case "billing":
        return "Billing & Subscription";
      case "settings":
        return "Settings";
      case "support":
        return "Support";
      default:
        return "Projects";
    }
  };

  return (
    <>
      {/* Background with gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-white to-primary-50/30" />

      <div className="min-h-screen relative">
        {/* Sidebar Container */}
        <div className="fixed inset-y-4 left-4 z-50 w-[320px]">
          {/* Sidebar with enhanced glass effect */}
          <div className="flex h-[calc(100vh-2rem)] flex-col bg-white/70 backdrop-blur-xl rounded-3xl shadow-large border border-white/50 overflow-hidden">
            {/* Logo Header */}
            <div className="flex h-20 items-center gap-3 px-6 border-b border-white/30 bg-gradient-to-r from-primary-50/50 to-secondary-50/50">
              <div className="relative">
                <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shadow-medium border border-gray-100">
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient
                        id="dashboardBlockGradient1"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          style={{ stopColor: "#6366F1", stopOpacity: 1 }}
                        />
                        <stop
                          offset="100%"
                          style={{ stopColor: "#8B5CF6", stopOpacity: 1 }}
                        />
                      </linearGradient>
                      <linearGradient
                        id="dashboardBlockGradient2"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          style={{ stopColor: "#0EA5E9", stopOpacity: 1 }}
                        />
                        <stop
                          offset="100%"
                          style={{ stopColor: "#06B6D4", stopOpacity: 1 }}
                        />
                      </linearGradient>
                      <linearGradient
                        id="dashboardBlockGradient3"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          style={{ stopColor: "#10B981", stopOpacity: 1 }}
                        />
                        <stop
                          offset="100%"
                          style={{ stopColor: "#34D399", stopOpacity: 1 }}
                        />
                      </linearGradient>
                      <filter
                        id="dashboardShadow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="1"
                          stdDeviation="1"
                          floodColor="rgba(0,0,0,0.1)"
                        />
                      </filter>
                    </defs>

                    {/* Building Blocks representing comments */}
                    {/* Top block (first comment) */}
                    <rect
                      x="3"
                      y="5"
                      width="6"
                      height="2.5"
                      rx="1.25"
                      fill="url(#dashboardBlockGradient1)"
                      filter="url(#dashboardShadow)"
                    />
                    <circle
                      cx="4.2"
                      cy="6.2"
                      r="0.6"
                      fill="white"
                      opacity="0.9"
                    />
                    <rect
                      x="5.2"
                      y="6"
                      width="2.5"
                      height="0.4"
                      rx="0.2"
                      fill="white"
                      opacity="0.7"
                    />

                    {/* Middle block (second comment) */}
                    <rect
                      x="5"
                      y="8.5"
                      width="7"
                      height="2.5"
                      rx="1.25"
                      fill="url(#dashboardBlockGradient2)"
                      filter="url(#dashboardShadow)"
                    />
                    <circle
                      cx="6.2"
                      cy="9.7"
                      r="0.6"
                      fill="white"
                      opacity="0.9"
                    />
                    <rect
                      x="7.2"
                      y="9.5"
                      width="3.5"
                      height="0.4"
                      rx="0.2"
                      fill="white"
                      opacity="0.7"
                    />

                    {/* Bottom block (third comment) */}
                    <rect
                      x="7"
                      y="12"
                      width="5"
                      height="2.5"
                      rx="1.25"
                      fill="url(#dashboardBlockGradient3)"
                      filter="url(#dashboardShadow)"
                    />
                    <circle
                      cx="8.2"
                      cy="13.2"
                      r="0.6"
                      fill="white"
                      opacity="0.9"
                    />
                    <rect
                      x="9.2"
                      y="13"
                      width="1.8"
                      height="0.4"
                      rx="0.2"
                      fill="white"
                      opacity="0.7"
                    />

                    {/* Comment bubble icon */}
                    <path
                      d="M15.5 7 C17 7 18 8 18 9.5 C18 11 17 12 15.5 12 L15 12 L14.2 13.5 L15.2 12 C14 12 13.5 11 13.5 9.5 C13.5 8 14.5 7 15.5 7 Z"
                      fill="#F59E0B"
                      stroke="white"
                      strokeWidth="0.3"
                      filter="url(#dashboardShadow)"
                    />

                    {/* Chat dots in bubble */}
                    <circle cx="14.8" cy="9.5" r="0.3" fill="white" />
                    <circle cx="15.5" cy="9.5" r="0.3" fill="white" />
                    <circle cx="16.2" cy="9.5" r="0.3" fill="white" />
                  </svg>
                </div>
                <div className="absolute -top-1 -right-1 h-4 w-4 bg-success-500 rounded-full border-2 border-white shadow-soft flex items-center justify-center">
                  <div className="h-1.5 w-1.5 bg-white rounded-full" />
                </div>
              </div>
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  FeedbackFlow
                </span>
                <div className="text-xs text-gray-500 font-medium">
                  {isSubscribed ? "Pro Plan" : "Free Trial"}
                </div>
              </div>
            </div>

            {/* User Info */}
            <div className="px-6 py-4 border-b border-white/30">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center shadow-medium">
                  <span className="text-white font-semibold text-sm">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user.email?.split("@")[0]}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-2 px-4 py-6">
              {navigation.map((item, index) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.section)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 group animate-fade-in-right",
                    section === item.section
                      ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 text-primary-700 shadow-soft border border-primary-200/50"
                      : "text-gray-700 hover:bg-white/50 hover:shadow-soft"
                  )}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div
                    className={cn(
                      "p-2 rounded-lg transition-all duration-200",
                      section === item.section
                        ? "bg-gradient-to-br from-primary-500 to-secondary-500 shadow-medium"
                        : "bg-gray-100 group-hover:bg-white group-hover:shadow-soft"
                    )}
                  >
                    <item.icon
                      className={cn(
                        "h-4 w-4 transition-colors",
                        section === item.section
                          ? "text-white"
                          : "text-gray-600 group-hover:text-gray-900"
                      )}
                    />
                  </div>
                  <span className="font-medium">{item.name}</span>
                  {section === item.section && (
                    <div className="ml-auto h-2 w-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" />
                  )}
                </button>
              ))}
            </nav>

            {/* Bottom Navigation */}
            <div className="border-t border-white/30 px-4 py-4 space-y-2">
              {bottomNavigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.section)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 group",
                    section === item.section
                      ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 text-primary-700 shadow-soft border border-primary-200/50"
                      : "text-gray-700 hover:bg-white/50 hover:shadow-soft"
                  )}
                >
                  <div
                    className={cn(
                      "p-2 rounded-lg transition-all duration-200",
                      section === item.section
                        ? "bg-gradient-to-br from-primary-500 to-secondary-500 shadow-medium"
                        : "bg-gray-100 group-hover:bg-white group-hover:shadow-soft"
                    )}
                  >
                    <item.icon
                      className={cn(
                        "h-4 w-4 transition-colors",
                        section === item.section
                          ? "text-white"
                          : "text-gray-600 group-hover:text-gray-900"
                      )}
                    />
                  </div>
                  <span className="font-medium">{item.name}</span>
                </button>
              ))}

              <button
                onClick={handleSignOut}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-error-600 hover:bg-error-50 hover:shadow-soft transition-all duration-200 group"
              >
                <div className="p-2 rounded-lg bg-error-100 group-hover:bg-error-200 transition-all duration-200">
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </div>
                <span className="font-medium">Sign out</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="pl-[340px]">
          <main className="p-8">
            {/* Page Header */}
            <div className="mb-8 animate-fade-in-up">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {getSectionTitle()}
              </h1>
              <p className="text-gray-600">
                {section === "projects" &&
                  "Manage your feedback projects and collaborate with your team."}
                {section === "billing" &&
                  "Manage your subscription and billing information."}
                {section === "settings" &&
                  "Customize your account and preferences."}
                {section === "support" &&
                  "Need help? Send us an email and we'll get back to you as soon as possible."}
              </p>
            </div>

            {/* Content */}
            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "0.1s" }}
            >
              {renderSection()}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};
